import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Card, CardContent, Typography, Button, Chip, TextField, Stack,
  Table, TableHead, TableBody, TableRow, TableCell, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, LinearProgress, Tooltip, Alert,
  CircularProgress, MenuItem, InputAdornment
} from "@mui/material";
import { Edit, Add, Receipt } from "@mui/icons-material";
import API from "../services/api";
import toast from "react-hot-toast";

const trMoney = (x) => Number(x || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " ₺";
const trDateTime = (x) => x ? new Date(x).toLocaleString("tr-TR") : "—";

export default function CariHesap() {
  const [users, setUsers]     = useState([]);
  const [search, setSearch]   = useState("");
  const [loading, setLoading] = useState(false);

  const [limitDialog, setLimitDialog] = useState({ open: false, user: null });
  const [limitValue, setLimitValue]   = useState("");

  const [txDialog, setTxDialog] = useState({ open: false, user: null });
  const [txForm, setTxForm]     = useState({ amount: "", type: "borç", note: "" });

  const [historyDialog, setHistoryDialog] = useState({ open: false, user: null, items: [], loading: false });

  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get("/api/users");
      const list = (r.data || []).filter(u =>
        (u.role || "").toLowerCase() === "customer" || u.role === "user" || !u.role
      );
      setUsers(list);
    } catch (e) {
      toast.error("Kullanıcılar yüklenemedi: " + (e?.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = users.filter(u => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (u.name || "").toLowerCase().includes(s)
        || (u.email || "").toLowerCase().includes(s)
        || (u.company || "").toLowerCase().includes(s);
  });

  const openLimitDialog = (u) => {
    setLimitValue(String(u.kredi_limiti ?? 0));
    setLimitDialog({ open: true, user: u });
  };

  const submitLimit = async () => {
    const u = limitDialog.user; if (!u) return;
    setSaving(true);
    try {
      await API.put(`/api/admin/users/${u.id}`, { kredi_limiti: Number(limitValue) });
      toast.success("Kredi limiti güncellendi");
      setLimitDialog({ open: false, user: null });
      load();
    } catch (e) {
      toast.error("Kayıt başarısız: " + (e?.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  };

  const openTxDialog = (u) => {
    setTxForm({ amount: "", type: "borç", note: "" });
    setTxDialog({ open: true, user: u });
  };

  const submitTx = async () => {
    const u = txDialog.user; if (!u) return;
    if (!txForm.amount || Number(txForm.amount) <= 0) {
      toast.error("Geçerli bir tutar girin"); return;
    }
    setSaving(true);
    try {
      await API.post("/api/admin/cari/transaction", {
        user_id: u.id,
        amount: Number(txForm.amount),
        type: txForm.type,
        note: txForm.note || null
      });
      toast.success(`${txForm.type === "borç" ? "Borç" : "Alacak"} kaydedildi`);
      setTxDialog({ open: false, user: null });
      load();
    } catch (e) {
      toast.error("Kayıt başarısız: " + (e?.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async (u) => {
    setHistoryDialog({ open: true, user: u, items: [], loading: true });
    try {
      const r = await API.get(`/api/admin/cari/transactions?user_id=${u.id}`);
      const items = Array.isArray(r.data) ? r.data : (r.data?.transactions || []);
      setHistoryDialog({ open: true, user: u, items, loading: false });
    } catch (e) {
      setHistoryDialog({ open: true, user: u, items: [], loading: false });
    }
  };

  const totalLimit  = users.reduce((s, u) => s + Number(u.kredi_limiti || 0), 0);
  const totalUsed   = users.reduce((s, u) => s + Number(u.bakiye || 0), 0);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={700}>💳 Cari Hesap Yönetimi</Typography>
        <Button variant="outlined" onClick={load} disabled={loading}>Yenile</Button>
      </Stack>

      <Stack direction="row" spacing={2} mb={2}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="caption" color="text.secondary">Toplam Kredi Limiti</Typography>
            <Typography variant="h6" fontWeight={700}>{trMoney(totalLimit)}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="caption" color="text.secondary">Toplam Kullanım (Bakiye)</Typography>
            <Typography variant="h6" fontWeight={700} color={totalUsed > totalLimit ? "error" : "primary"}>
              {trMoney(totalUsed)}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="caption" color="text.secondary">Kalan Limit</Typography>
            <Typography variant="h6" fontWeight={700} color="success.main">
              {trMoney(Math.max(0, totalLimit - totalUsed))}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="caption" color="text.secondary">Müşteri Sayısı</Typography>
            <Typography variant="h6" fontWeight={700}>{users.length}</Typography>
          </CardContent>
        </Card>
      </Stack>

      <Card>
        <CardContent>
          <Stack direction="row" spacing={2} mb={2}>
            <TextField
              size="small"
              fullWidth
              placeholder="Müşteri ara (ad, e-posta, firma)..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </Stack>

          {loading ? (
            <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress /></Box>
          ) : filtered.length === 0 ? (
            <Alert severity="info">Müşteri bulunamadı.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ad Soyad</TableCell>
                  <TableCell>Firma</TableCell>
                  <TableCell>E-posta</TableCell>
                  <TableCell align="right">Kredi Limiti</TableCell>
                  <TableCell align="right">Bakiye</TableCell>
                  <TableCell align="right">Kalan</TableCell>
                  <TableCell align="center" sx={{ minWidth: 180 }}>Durum</TableCell>
                  <TableCell align="center">İşlem</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(u => {
                  const limit = Number(u.kredi_limiti || 0);
                  const bal   = Number(u.bakiye || 0);
                  const remain = limit - bal;
                  const pct = limit > 0 ? Math.min(100, (bal / limit) * 100) : 0;
                  const danger = limit > 0 && bal > limit;
                  const warn   = limit > 0 && pct >= 80 && !danger;
                  const color  = danger ? "error" : (warn ? "warning" : "success");

                  return (
                    <TableRow key={u.id} hover>
                      <TableCell>{u.name}</TableCell>
                      <TableCell>{u.company || "—"}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell align="right">{trMoney(limit)}</TableCell>
                      <TableCell align="right">{trMoney(bal)}</TableCell>
                      <TableCell align="right" sx={{ color: remain < 0 ? "error.main" : "inherit", fontWeight: 600 }}>
                        {trMoney(remain)}
                      </TableCell>
                      <TableCell align="center">
                        {limit > 0 ? (
                          <Box>
                            <LinearProgress
                              variant="determinate"
                              value={pct}
                              color={color}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                            <Typography variant="caption" color={color + ".main"}>
                              %{pct.toFixed(0)} kullanılmış
                            </Typography>
                          </Box>
                        ) : (
                          <Chip size="small" label="Limit yok" color="default" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Limit Düzenle">
                          <IconButton size="small" color="primary" onClick={() => openLimitDialog(u)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Borç/Alacak Girişi">
                          <IconButton size="small" color="secondary" onClick={() => openTxDialog(u)}>
                            <Add fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Hareketler">
                          <IconButton size="small" onClick={() => openHistory(u)}>
                            <Receipt fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Limit Dialog */}
      <Dialog open={limitDialog.open} onClose={() => setLimitDialog({ open: false, user: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Kredi Limiti Düzenle</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography>{limitDialog.user?.name} ({limitDialog.user?.company || "—"})</Typography>
            <TextField
              label="Kredi Limiti"
              type="number"
              value={limitValue}
              onChange={e => setLimitValue(e.target.value)}
              autoFocus
              fullWidth
              InputProps={{ endAdornment: <InputAdornment position="end">₺</InputAdornment> }}
              inputProps={{ step: "0.01", min: 0 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLimitDialog({ open: false, user: null })}>İptal</Button>
          <Button variant="contained" onClick={submitLimit} disabled={saving}>
            {saving ? "..." : "Kaydet"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction Dialog */}
      <Dialog open={txDialog.open} onClose={() => setTxDialog({ open: false, user: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Borç / Alacak Girişi</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography>{txDialog.user?.name} ({txDialog.user?.company || "—"})</Typography>
            <TextField
              select
              label="İşlem Tipi"
              value={txForm.type}
              onChange={e => setTxForm({ ...txForm, type: e.target.value })}
              fullWidth
            >
              <MenuItem value="borç">Borç (bakiye artar)</MenuItem>
              <MenuItem value="alacak">Alacak (bakiye azalır)</MenuItem>
            </TextField>
            <TextField
              label="Tutar"
              type="number"
              value={txForm.amount}
              onChange={e => setTxForm({ ...txForm, amount: e.target.value })}
              fullWidth
              autoFocus
              InputProps={{ endAdornment: <InputAdornment position="end">₺</InputAdornment> }}
              inputProps={{ step: "0.01", min: 0 }}
            />
            <TextField
              label="Açıklama"
              value={txForm.note}
              onChange={e => setTxForm({ ...txForm, note: e.target.value })}
              multiline
              rows={2}
              fullWidth
              placeholder="Fatura no, açıklama, vb."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTxDialog({ open: false, user: null })}>İptal</Button>
          <Button variant="contained" onClick={submitTx} disabled={saving}>
            {saving ? "..." : "Kaydet"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialog.open} onClose={() => setHistoryDialog({ open: false, user: null, items: [], loading: false })} maxWidth="md" fullWidth>
        <DialogTitle>Cari Hareketler — {historyDialog.user?.name}</DialogTitle>
        <DialogContent dividers>
          {historyDialog.loading ? (
            <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress /></Box>
          ) : historyDialog.items.length === 0 ? (
            <Alert severity="info">Bu müşterinin henüz cari hareketi yok.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tarih</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell>Açıklama</TableCell>
                  <TableCell align="right">Tutar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyDialog.items.map((tx, i) => (
                  <TableRow key={i}>
                    <TableCell>{trDateTime(tx.created_at)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={tx.type} color={tx.type === "borç" ? "error" : "success"} />
                    </TableCell>
                    <TableCell>{tx.note || "—"}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{trMoney(tx.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialog({ open: false, user: null, items: [], loading: false })}>Kapat</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
